"use client";

import type { Org } from "shared";

import { useOrgContext } from "../context/OrgContext";

/**
 * Organization Hooks Module
 *
 * Provides hooks for accessing organization context throughout the application:
 *
 * - `useCurrentOrg()` - Get org data (returns null when loading/error)
 * - `useOrgUid()` - Get org UID (returns null when loading/error)
 * - `useOrgContext()` - Get org with loading/error states (for manual handling)
 * - `useOrgLoading()` - Check if org is loading
 * - `useOrgError()` - Get org error state
 *
 * @example
 * // For components that need org data
 * function MyComponent() {
 *   const org = useCurrentOrg();
 *
 *   if (!org) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   return <div>{org.name}</div>;
 * }
 *
 * @example
 * // For components that handle loading states manually
 * function MyComponent() {
 *   const { org, loading, error } = useOrgContext();
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   if (!org) return <NoOrg />;
 *
 *   return <div>{org.name}</div>;
 * }
 */

/**
 * Hook to get the current organization
 *
 * This hook provides the current org data and returns null if the org
 * is still loading, has an error, or is not found.
 *
 * @returns The current organization object or null if not ready
 *
 * @example
 * function MyComponent() {
 *   const org = useCurrentOrg();
 *
 *   if (!org) {
 *     return <div>Loading organization...</div>;
 *   }
 *
 *   return <div>{org.name}</div>;
 * }
 */
export function useCurrentOrg(): Org | null {
  const { org, loading, error } = useOrgContext();

  if (loading || error || !org) {
    return null;
  }

  return org;
}

/**
 * Hook to get the current organization UID
 *
 * This hook provides just the org UID string and returns null if not available.
 * Use this when you only need the org UID for data queries.
 *
 * @returns The current organization UID or null if not ready
 *
 * @example
 * function MyComponent() {
 *   const orgUid = useOrgUid();
 *
 *   if (!orgUid) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   const groupsPath = `orgs/${orgUid}/groups`;
 *   // ... use path for Firestore query
 * }
 */
export function useOrgUid(): string | null {
  const { orgUid, loading, error } = useOrgContext();

  if (loading || error || !orgUid) {
    return null;
  }

  return orgUid;
}

/**
 * Hook to check if organization is loading
 *
 * @returns true if organization is loading, false otherwise
 */
export function useOrgLoading(): boolean {
  const { loading } = useOrgContext();
  return loading;
}

/**
 * Hook to get organization error state
 *
 * @returns Error if organization failed to load, null otherwise
 */
export function useOrgError(): Error | null {
  const { error } = useOrgContext();
  return error;
}
